import React, { useContext } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
// Import Contexts
import { ProgramConfigContext } from '../../contexts/ProgramConfigContext';
// Import Utils
import { hasTransfersEnabled } from '../../utils/transfers';
// Import Local Routes
import SelectTransfer from './routes/SelectTransfer';
import PersonalAccountTransfer from './routes/PersonalAccountTransfer';
import AccountToAccountTransfer from './routes/AccountToAccountTransfer';
import PersonToPersonTransfer from './routes/PersonToPersonTransfer';
import PersonToPersonAccounts from './routes/PersonToPersonAccounts';
import RecipientAccounts from './routes/RecipientAccounts';
import PersonToExternalTransfer from './routes/PersonToExternalTransfer';
import EtransferPush from './routes/EtransferPush';
import PTCEtransferPush from './routes/PTCEtransferPush';
import EtransferPull from './routes/EtransferPull';
import DirectsendPush from './routes/DirectsendPush';
import DirectsendPull from './routes/DirectsendPull';
// Import Local Components
import TransferRouteGuard from './components/Route';
import ExternalAccounts from './routes/ExternalAccounts';
import ExternalAccountDetails from './routes/ExternalAccounts/routes/ExternalAccountDetails';
import TransferHistory from './routes/TransferHistory';

const Transfer = () => {
  const configs = useContext(ProgramConfigContext);

  // The Transfers link is hidden from the nav when no transfer type is enabled;
  // block the route too so a direct URL sends the cardholder back to the dashboard.
  if (!hasTransfersEnabled(configs)) {
    return <Navigate to="/" replace />;
  }

  return (
    <Routes>
      <Route
        path="my-account"
        element={
          <TransferRouteGuard enabled={configs.allow_personal_bank_account_transfers}>
            <PersonalAccountTransfer />
          </TransferRouteGuard>
        }
      />
      <Route
        path="card-to-card/accounts"
        element={
          <TransferRouteGuard enabled={configs.allow_account_to_account_transfer}>
            <RecipientAccounts />
          </TransferRouteGuard>
        }
      />
      <Route
        path="card-to-card"
        element={
          <TransferRouteGuard enabled={configs.allow_account_to_account_transfer || configs.allow_child_cards}>
            <AccountToAccountTransfer configs={configs} />
          </TransferRouteGuard>
        }
      />
      <Route
        path="person-to-person/accounts"
        element={
          <TransferRouteGuard enabled={configs.allow_person_to_person_transfers}>
            <PersonToPersonAccounts />
          </TransferRouteGuard>
        }
      />
      <Route
        path="person-to-person"
        element={
          <TransferRouteGuard enabled={configs.allow_person_to_person_transfers}>
            <PersonToPersonTransfer />
          </TransferRouteGuard>
        }
      />
      <Route
        path="person-to-external"
        element={
          <TransferRouteGuard enabled={configs.allow_interac_pull_transfers}>
            <PersonToExternalTransfer />
          </TransferRouteGuard>
        }
      />
      <Route
        path="etransfer-push"
        element={
          <TransferRouteGuard enabled={configs.allow_external_transfers_v2 && configs.allow_etransfer_push_v2}>
            {configs.allow_ptc_etransfer_push ?
              <PTCEtransferPush configs={configs} /> :
              <EtransferPush configs={configs} />
            }
          </TransferRouteGuard>
        }
      />
      <Route
        path="etransfer-pull"
        element={
          <TransferRouteGuard enabled={configs.allow_external_transfers_v2}>
            <EtransferPull />
          </TransferRouteGuard>
        }
      />
      <Route
        path="directsend-push"
        element={
          <TransferRouteGuard enabled={configs.allow_external_transfers_v2}>
            <DirectsendPush />
          </TransferRouteGuard>
        }
      />
      <Route
        path="directsend-pull"
        element={
          <TransferRouteGuard enabled={configs.allow_external_transfers_v2}>
            <DirectsendPull />
          </TransferRouteGuard>
        }
      />
      <Route
        path="transfer-history"
        element={<TransferHistory />}
      />
      <Route
        path="external-accounts"
        element={
          <TransferRouteGuard enabled={configs.allow_external_transfers_v2}>
            <ExternalAccounts />
          </TransferRouteGuard>
        }
      />
      <Route
        path="external-accounts/:id"
        element={
          <TransferRouteGuard enabled={configs.allow_external_transfers_v2}>
            <ExternalAccountDetails />
          </TransferRouteGuard>
        }
      />
      <Route
        path="/"
        element={<SelectTransfer configs={configs} />}
      />
    </Routes>
  );
};

export default Transfer;
